import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useUpgradeModal } from "../context/UpgradeModalContext";
import { Plus, PencilSimple, Trash, X, Users as UsersIcon, ArrowUp } from "@phosphor-icons/react";

const ROLE_BADGE = {
  admin: "bg-[#1A2332] text-white",
  warehouse_staff: "bg-amber-400 text-amber-950",
  viewer: "bg-zinc-200 text-zinc-700",
};

export default function Users() {
  const { user: me } = useAuth();
  const { openUpgradeModal } = useUpgradeModal();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "viewer" });
  const [saving, setSaving] = useState(false);

  const plan = me?.plan || {};
  const userLimit = plan.user_limit || null;
  const userCount = items.length;
  const atLimit = userLimit != null && userCount >= userLimit;
  const allowedRoles = plan.allowed_roles || ["admin", "warehouse_staff", "viewer"];
  const currentTier = plan.tier || "basic";
  const showUpgrade = !plan.is_demo && plan.tier && plan.tier !== "pro";

  const load = async () => {
    const { data } = await api.get("/users");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const onOpen = (u) => {
    setEdit(u || null);
    setForm(u
      ? { name: u.name, email: u.email, password: "", role: u.role }
      : { name: "", email: "", password: "", role: "viewer" });
    setOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (edit) {
        const patch = { name: form.name, role: form.role };
        if (form.password) patch.password = form.password;
        await api.patch(`/users/${edit.id}`, patch);
        toast.success("User updated");
      } else {
        await api.post("/auth/register", {
          name: form.name,
          email: form.email,
          password: form.password,
          role: form.role,
        });
        toast.success("User created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (u) => {
    if (!window.confirm(`Delete user "${u.email}"?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#FF9800]">Access Control</div>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-zinc-900 mt-1">
            Users
          </h1>
        </div>
        <button
          onClick={() => onOpen(null)}
          disabled={atLimit}
          data-testid="add-user-button"
          title={atLimit ? `Your plan supports up to ${userLimit} users` : ""}
          className="inline-flex items-center gap-2 bg-[#FF9800] hover:bg-[#E68900] text-white px-4 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold w-fit disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus size={16} weight="bold" /> Invite user
        </button>
      </div>

      {showUpgrade && (
        <div
          data-testid="user-plan-notice"
          className={`border px-4 py-3 text-xs sm:text-sm flex flex-col sm:flex-row sm:items-center gap-3 ${
            atLimit ? "bg-amber-50 border-amber-300 text-amber-900" : "bg-zinc-100 border-zinc-300 text-zinc-700"
          }`}
        >
          <div className="flex items-start gap-2 flex-1">
            <span className="font-bold uppercase tracking-widest text-[10px] mt-0.5">
              {plan.label?.replace("ezItemizer ", "") || currentTier} plan
            </span>
            <span>
              {userCount} of {userLimit} users · roles: {allowedRoles.map((r) => r.replace("_", " ")).join(", ")}.
              {atLimit && " You've reached the user limit on this plan."}
            </span>
          </div>
          <button
            type="button"
            onClick={() =>
              openUpgradeModal({
                reason: atLimit ? "user_limit_reached" : "feature_not_in_plan",
                message: atLimit
                  ? `You've reached the ${userLimit}-user limit on your current plan.`
                  : "Unlock more users and roles by upgrading your plan.",
                currentTier,
              })
            }
            data-testid="users-upgrade-button"
            className="inline-flex items-center justify-center gap-1.5 bg-[#FF9800] hover:bg-[#E68900] text-[#1A2332] font-bold text-[10px] uppercase tracking-widest px-3 py-2 transition-colors flex-shrink-0 w-fit"
          >
            <ArrowUp size={12} weight="bold" />
            Upgrade plan
          </button>
        </div>
      )}

      <div className="bg-white border border-zinc-300 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
              <th className="text-left px-3 sm:px-4 py-3 border-b-2 border-zinc-900">Name</th>
              <th className="text-left px-3 sm:px-4 py-3 border-b-2 border-zinc-900 hidden sm:table-cell">Email</th>
              <th className="text-left px-3 sm:px-4 py-3 border-b-2 border-zinc-900">Role</th>
              <th className="text-left px-3 sm:px-4 py-3 border-b-2 border-zinc-900 hidden md:table-cell">Created</th>
              <th className="text-right px-3 sm:px-4 py-3 border-b-2 border-zinc-900 w-24 sm:w-32">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-12 text-center">
                  <UsersIcon size={28} weight="duotone" className="mx-auto text-zinc-300" />
                </td>
              </tr>
            )}
            {items.map((u) => (
              <tr key={u.id} className="row-hover" data-testid={`user-row-${u.email}`}>
                <td className="px-3 sm:px-4 py-3 border-b border-zinc-200 font-medium">
                  <div>{u.name}</div>
                  <div className="text-[10px] text-zinc-500 font-mono sm:hidden mt-0.5 truncate max-w-[160px]">{u.email}</div>
                </td>
                <td className="px-3 sm:px-4 py-3 border-b border-zinc-200 font-mono text-xs hidden sm:table-cell">{u.email}</td>
                <td className="px-3 sm:px-4 py-3 border-b border-zinc-200">
                  <span className={`px-1.5 sm:px-2 py-0.5 text-[9px] sm:text-[10px] font-bold uppercase tracking-wider ${ROLE_BADGE[u.role]}`}>
                    {u.role.replace("_", " ")}
                  </span>
                </td>
                <td className="px-3 sm:px-4 py-3 border-b border-zinc-200 font-mono text-xs text-zinc-500 hidden md:table-cell">
                  {new Date(u.created_at).toLocaleDateString()}
                </td>
                <td className="px-3 sm:px-4 py-3 border-b border-zinc-200 text-right">
                  <div className="inline-flex items-center divide-x divide-zinc-300 border border-zinc-300">
                    <button onClick={() => onOpen(u)} data-testid={`edit-user-${u.id}`} className="px-2.5 py-1.5 hover:bg-[#1A2332] hover:text-white">
                      <PencilSimple size={14} weight="bold" />
                    </button>
                    <button
                      onClick={() => onDelete(u)}
                      disabled={u.id === me?.id}
                      data-testid={`delete-user-${u.id}`}
                      className="px-2.5 py-1.5 hover:bg-red-600 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-[#1A2332]/60 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white border border-zinc-300 w-full max-w-md">
            <div className="flex items-center justify-between border-b border-zinc-300 px-6 py-4">
              <h2 className="font-display text-xl font-semibold">{edit ? "Edit user" : "Invite user"}</h2>
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-zinc-100"><X size={18} weight="bold" /></button>
            </div>
            <form onSubmit={onSubmit} className="px-6 py-5 space-y-4">
              <label className="block">
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-700">Name *</span>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" className="mt-1.5 w-full px-4 py-2.5 border border-zinc-300 focus:border-[#FF9800] focus:outline-none focus:ring-1 focus:ring-[#FF9800]" />
              </label>
              <label className="block">
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-700">Email *</span>
                <input
                  required
                  type="email"
                  disabled={!!edit}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="user-email-input"
                  className="mt-1.5 w-full px-4 py-2.5 border border-zinc-300 focus:border-[#FF9800] focus:outline-none focus:ring-1 focus:ring-[#FF9800] font-mono disabled:bg-zinc-100"
                />
              </label>
              <label className="block">
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-700">
                  Password {edit ? "(leave blank to keep)" : "*"}
                </span>
                <input
                  type="password"
                  required={!edit}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  data-testid="user-password-input"
                  className="mt-1.5 w-full px-4 py-2.5 border border-zinc-300 focus:border-[#FF9800] focus:outline-none focus:ring-1 focus:ring-[#FF9800] font-mono"
                  minLength={6}
                />
              </label>
              <label className="block">
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-700">Role</span>
                <select
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  data-testid="user-role-select"
                  className="mt-1.5 w-full px-4 py-2.5 border border-zinc-300 focus:border-[#FF9800] focus:outline-none focus:ring-1 focus:ring-[#FF9800]"
                >
                  <option value="viewer">Viewer</option>
                  {allowedRoles.includes("warehouse_staff") && (
                    <option value="warehouse_staff">Warehouse Staff</option>
                  )}
                  <option value="admin">Admin</option>
                </select>
              </label>
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-200">
                <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 text-sm font-semibold border border-zinc-300 hover:bg-zinc-100">Cancel</button>
                <button type="submit" disabled={saving} data-testid="user-save-button" className="px-5 py-2 text-sm font-semibold bg-[#FF9800] hover:bg-[#E68900] text-white">
                  {saving ? "Saving…" : edit ? "Save" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
